import { useParams } from 'react-router-dom';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@src/design-system/avatar';
import { cn } from '@src/design-system/helpers';
import { useGetMemberships } from '@src/shared/services/get-memberships';

export function getOrganizationInitials(name: string) {
  const trimmed = name.trim();
  if (!trimmed) return '—';

  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0] ?? ''}${words[1][0] ?? ''}`.toUpperCase();
  }

  return trimmed.slice(0, 2).toUpperCase();
}

export function useCurrentOrganizationPreview() {
  const { organizationSlug } = useParams();
  const { data: memberships } = useGetMemberships();

  const organization = memberships?.find(
    (membership) => membership.organization.slug === organizationSlug
  )?.organization;

  const name = organization?.name ?? '';

  return {
    organization,
    name,
    avatar: organization?.avatar,
    id: organization?.id ?? 0,
    initials: getOrganizationInitials(name),
  };
}

type OrganizationPreviewAvatarProps = {
  className?: string;
  imageClassName?: string;
  fallbackClassName?: string;
};

export function OrganizationPreviewAvatar({
  className,
  imageClassName,
  fallbackClassName,
}: OrganizationPreviewAvatarProps) {
  const { name, avatar, id, initials } = useCurrentOrganizationPreview();

  return (
    <Avatar className={cn('shrink-0', className)}>
      {avatar ? (
        <AvatarImage
          src={avatar}
          alt={name || 'Organization'}
          className={cn('object-cover', imageClassName)}
        />
      ) : null}
      <AvatarFallback
        colorId={id}
        className={fallbackClassName}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
